// ============================================================================
// client/baroudeur-papier.ts — LE BAROUDEUR EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Branche le pantin articulé de client/papier.ts sur le contrat que le jeu
// attend déjà : drawBaroudeur(ctx, pose, phase, face, x, y, ts, opts). Les cinq
// appelants (render, mine-render, dash-fx, mine, vestiaire) ne changent pas.
//
// Deux choses seulement se passent ici :
//   1. la TRADUCTION (pose du jeu, phase 0..1) -> deux poses clés du pantin et
//      un curseur entre elles, puisqu'une pose n'est qu'une liste de nombres ;
//   2. la TRADUCTION des cosmétiques (teinte, tenue, chapeau) en couleurs de
//      papier.
//
// ⚠️ PERFORMANCE. Le papier découpé coûte cher : chaque pièce porte une ombre,
// un motif imprimé et un grain de fibre, soit des milliers de petits fillRect.
// Le dessiner à chaque image pour trois joueurs mettrait le jeu à genoux. Tout
// passe donc par un CACHE : une pose est peinte une fois dans un canvas hors
// écran, puis simplement recopiée. La phase est quantifiée pour que le cache
// ait un nombre fini d'entrées.
// ============================================================================

import { accessoireById } from '../shared/accessoires'
import { outfitById } from '../shared/outfits'
import { AVATAR_HUES } from '../shared/skins'
import { chapeau4, pantin, POSES, melange, type Pose, type Tenue } from './papier'

type Ctx = CanvasRenderingContext2D

export type PoseName =
  | 'idle' | 'run' | 'climb' | 'rope' | 'fall' | 'dig' | 'crouch'
  | 'dash' | 'brake' | 'pickUp' | 'pickHit' | 'tramp0' | 'tramp1' | 'tramp2'

export interface DrawOpts {
  dashVariant?: number
  hue?: number
  outfit?: string
  hat?: string
  hatTime?: number
}

/* --------------------------------------------------------------- les cycles */
/* Chaque pose du jeu est une suite de poses clés du pantin. Le jeu fournit une
   phase entre 0 et 1 ; on en déduit le couple de clés et le curseur entre eux.
   Une seule clé = pose fixe. */
const CYCLES: Record<PoseName, readonly string[]> = {
  idle: ['idle'],
  run: ['run0', 'run1', 'run2', 'run3', 'run4', 'run5'],
  climb: ['climb0', 'climb1', 'climb2', 'climb1'],
  rope: ['rope0', 'rope1', 'rope2', 'rope1'],
  fall: ['fall', 'fallA'],
  dig: ['dig', 'digA', 'pickHit'],
  crouch: ['crouch'],
  dash: ['dash0', 'dashA', 'dash1'],
  brake: ['brake'],
  pickUp: ['pickUp'],
  pickHit: ['pickHit'],
  tramp0: ['tramp0'],
  tramp1: ['tramp1'],
  tramp2: ['tramp2'],
}

/** Nombre de pas de phase retenus par cycle : c'est ce qui borne le cache.
 *  12 pas à 30 Hz suffisent largement — l'œil ne distingue pas plus fin sur un
 *  personnage de 44 pixels de haut. */
const PAS = 12

function poseDuJeu(pose: PoseName, phase: number): Pose {
  const cycle = CYCLES[pose] ?? CYCLES.idle
  const n = cycle.length
  if (n === 1) return POSES[cycle[0]!]!
  const ph = ((phase % 1) + 1) % 1
  const brut = ph * n
  const i = Math.min(n - 1, Math.floor(brut))
  return melange(POSES[cycle[i]!]!, POSES[cycle[(i + 1) % n]!]!, brut - i)
}

/* ------------------------------------------------------------- les couleurs */
/* La tenue d'origine : la veste ocre du Mineur validée le 16/07. */
const ORIGINE: Tenue = { torse: '#c08a3a', jambe: '#5a4020', motif: 'plis', accent: '#ffcf1f' }

/* Un motif de papier par tenue : c'est lui, et pas seulement la couleur, qui
   distingue « mousse » d'« ardoise » (cf. la planche des tenues de la maquette). */
const MOTIFS: Record<string, string> = {
  'tenue-charbon': 'grain', 'tenue-camouflage': 'marbre', 'tenue-glace': 'chevrons',
  'tenue-abysse': 'plis', 'tenue-lave': 'marbre', 'tenue-scaphandre': 'quadrille',
  'tenue-neon': 'rayures', 'tenue-or': 'guilloche', 'tenue-rouille': 'grain',
  'tenue-ardoise': 'quadrille', 'tenue-mousse': 'pois', 'tenue-porcelaine': 'guilloche',
  'tenue-braise': 'marbre', 'tenue-pourpre': 'chevrons', 'tenue-nacre': 'guilloche',
  'tenue-prisme': 'rayures',
}

/** Rotation de teinte, en degrés, sur une couleur hexadécimale. Sert à la
 *  palette de sept teintes de la veste, qui existait avant les tenues. */
function tourne(hex: string, deg: number): string {
  const n = parseInt(hex.slice(1), 16)
  let r = (n >> 16 & 255) / 255, g = (n >> 8 & 255) / 255, b = (n & 255) / 255
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn
  let h = 0
  if (d) {
    if (mx === r) h = ((g - b) / d) % 6
    else if (mx === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
  }
  h = (h * 60 + deg + 360) % 360
  const l = (mx + mn) / 2, s = d ? d / (1 - Math.abs(2 * l - 1)) : 0
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), m = l - c / 2
  const t: [number, number, number] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
  ;[r, g, b] = [t[0] + m, t[1] + m, t[2] + m]
  const q = (v: number): string => Math.max(0, Math.min(255, Math.round(v * 255))).toString(16).padStart(2, '0')
  return '#' + q(r) + q(g) + q(b)
}

export function tenueDuJeu(hue: number, outfit: string): Tenue {
  /* base : la tenue équipée, sinon la veste ocre du Mineur */
  let t: Tenue = ORIGINE
  if (outfit) {
    const o = outfitById(outfit)
    /* les cinq tons vont du plus sombre au plus clair : le pantalon dans les
       sombres, la veste au milieu, l'accent dans le clair */
    if (o) t = { torse: o.tons[2] ?? ORIGINE.torse, jambe: o.tons[1] ?? ORIGINE.jambe,
                 motif: MOTIFS[outfit] ?? 'plis', accent: o.tons[4] ?? ORIGINE.accent }
  }
  /* la TEINTE se cumule avec la tenue, comme le faisait l'ancien rendu pixel :
     la sortir de l'équation ferait perdre au joueur son choix de couleur dès
     qu'il équipe une tenue, sans le moindre message. */
  const deg = AVATAR_HUES[hue] ?? -1
  if (deg < 0) return t
  return { torse: tourne(t.torse, deg), jambe: tourne(t.jambe, deg),
           motif: t.motif, accent: t.accent }
}

/** Les chapeaux du jeu s'appellent « chapeau-tricorne » ; le pantin connaît
 *  « tricorne ». Sans accessoire, c'est le casque de mineur. */
/** Un accessoire, décomposé en ce qui REMPLACE le casque et ce qui s'AJOUTE
 *  par-dessus (auréole, couronne de fleurs : `remplace: false`). */
export function chapeauDuJeu(hat: string): { base: string; ajout: string } {
  if (!hat) return { base: 'casque', ajout: '' }
  const a = accessoireById(hat)
  if (!a) return { base: 'casque', ajout: '' }
  const court = hat.replace(/^chapeau-/, '')
  return a.remplace ? { base: court, ajout: '' } : { base: 'casque', ajout: court }
}

/* ----------------------------------------------------------------- le cache */
interface Rendu { cv: HTMLCanvasElement; base: number; cx: number; T: number }
const CACHE = new Map<string, Rendu>()
const CACHE_MAX = 240

/* La marge autour du pantin : la pioche, les bras tendus et les bois de cerf
   sortent largement de la case. Mesurées sur les poses les plus débordantes. */
const MARGE_X = 1.15, MARGE_HAUT = 0.75, MARGE_BAS = 0.18

function rendu(pose: PoseName, pas: number, hue: number, outfit: string, hat: string, T: number): Rendu {
  return renduPose(poseDuJeu(pose, pas / PAS), `${pose}|${pas}`, pas, hue, outfit, hat, T)
}

/** Le rendu à partir d'une Pose DÉJÀ RÉSOLUE (06/08) : c'est ce qui permet au porteur de
 *  maillet d'avoir ses propres postures, écrites hors du catalogue des poses du jeu.
 *  `cle` doit identifier la pose de façon stable — elle entre dans le cache. */
function renduPose(p: Pose, cle0: string, pas: number, hue: number, outfit: string, hat: string, T: number): Rendu {
  const cle = `${cle0}|${hue}|${outfit}|${hat}|${T}`
  const vieux = CACHE.get(cle)
  if (vieux) return vieux

  const w = Math.ceil(T * (1 + MARGE_X * 2)), h = Math.ceil(T * (1 + MARGE_HAUT + MARGE_BAS))
  const cv = document.createElement('canvas')
  cv.width = w; cv.height = h
  const g = cv.getContext('2d')
  const r: Rendu = { cv, base: Math.round(T * (1 + MARGE_HAUT)), cx: Math.round(w / 2), T }
  if (g) {
    g.imageSmoothingEnabled = true
    const tenue = tenueDuJeu(hue, outfit)
    const { base, ajout } = chapeauDuJeu(hat)
    pantin(g, r.cx, r.base, T, tenue, base, p, { tick: pas * 3 })
    /* l'accessoire ADDITIF se pose au-dessus du crâne, dans le même repère que
       la tête : sommet du casque = base - 44 u, décalé comme elle. */
    if (ajout) {
      const u = T / 44, TE = (p.tete ?? [0, 0, 0]) as [number, number, number]
      g.save()
      g.translate(r.cx + TE[0] * u * .5, r.base + TE[1] * u); g.rotate(TE[2])
      chapeau4(g, 1 * u, -47 * u, u, ajout, tenue)
      g.restore()
    }
  }
  if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
  CACHE.set(cle, r)
  return r
}

/**
 * Dessine Le Baroudeur à la pose donnée, dans la case ts×ts au coin (x, y).
 * Même contrat que l'ancien rendu pixel : les appelants n'ont rien à changer.
 */
export function drawBaroudeurPapier(
  ctx: Ctx, pose: PoseName, phase: number, face: 1 | -1,
  x: number, y: number, ts: number, opts?: DrawOpts,
): void {
  /* 36 × 44 dans une case de 48 : le rapport validé dans la maquette. */
  const T = Math.max(8, Math.round(ts * (44 / 48)))
  const ph = ((phase % 1) + 1) % 1
  const pas = Math.round(ph * PAS) % PAS
  const r = rendu(pose, pas, opts?.hue ?? 0, opts?.outfit ?? '', opts?.hat ?? '', T)

  const piedX = x + ts * 0.5
  const piedY = y + ts * 0.99
  ctx.save()
  if (face < 0) { ctx.translate(piedX, 0); ctx.scale(-1, 1); ctx.translate(-piedX, 0) }
  ctx.drawImage(r.cv, piedX - r.cx, piedY - r.base)
  ctx.restore()
}

/** Le Baroudeur dans une posture SUR MESURE (06/08, le porteur de maillet). Même contrat que
 *  drawBaroudeurPapier, mais la pose est fournie telle quelle au lieu d'être nommée. */
export function drawBaroudeurPose(
  ctx: Ctx, pose: Pose, cle: string, face: 1 | -1,
  x: number, y: number, ts: number, opts?: DrawOpts,
): void {
  const T = Math.max(8, Math.round(ts * (44 / 48)))
  const r = renduPose(pose, cle, 0, opts?.hue ?? 0, opts?.outfit ?? '', opts?.hat ?? '', T)
  const piedX = x + ts * 0.5
  const piedY = y + ts * 0.99
  ctx.save()
  if (face < 0) { ctx.translate(piedX, 0); ctx.scale(-1, 1); ctx.translate(-piedX, 0) }
  ctx.drawImage(r.cv, piedX - r.cx, piedY - r.base)
  ctx.restore()
}

export const BAROUDEUR_POSES: readonly PoseName[] = [
  'idle', 'run', 'climb', 'rope', 'fall', 'dig', 'crouch',
  'dash', 'brake', 'pickUp', 'pickHit', 'tramp0', 'tramp1', 'tramp2',
]
